import { useParams } from 'react-router-dom';
import { buildLinkUrl } from '../links/Link';
import { ADMIN_PAGE_ID_PROPERTY_OVERRIDES, ADMIN_ROOT, DEFAULT_ID_PROPERTY } from './AdminPages';

// re-export this so we don't have to import two files whenever we link for admin pages
export { AdminPages } from './AdminPages';

/**
 * Builds the root target configuration for an admin page
 *
 *     buildRootPageTarget(AdminPages.Users); // => '/admin/users'
 *
 * @param {String} adminPage
 */
export function buildRootPageTarget(adminPage) {
    return `${ADMIN_ROOT}/${adminPage}`;
}

/**
 * Builds the target configuration for an admin page with a mappable ID
 *
 *     buildPageTarget(AdminPages.Users); // => '/admin/users/:_id'
 *
 * @param {String} adminPage
 */
export function buildPageTarget(adminPage) {
    const idProp = ADMIN_PAGE_ID_PROPERTY_OVERRIDES[adminPage] || DEFAULT_ID_PROPERTY;

    return buildPageTargetWithIdProp(adminPage, idProp);
}

/**
 * Builds the target configuration for an admin page with idProp
 *     buildPageTarget(AdminPages.Collectors, _plcId); // => '/admin/collectors/:_plcId'
 *
 * @param {String} adminPage
 * @param {String} idProp
 */
export function buildPageTargetWithIdProp(adminPage, idProp) {
    return `${buildRootPageTarget(adminPage)}/:${idProp}`;
}

/**
 * Builds the target configuration for an admin page with path and idProp
 *     buildPageTarget(AdminPages.ServiceTests, path, _plcId); // => '/admin/service-tests/Web/tests/450dca20-0791-11f1-92e4-7b67528b854f'
 *
 * @param {String} adminPage
 * @param {String} path
 * @param {String} idProp
 */
export function buildPageTargetWithPathAndIdProp(adminPage, path, idProp) {
    return `${buildRootPageTarget(adminPage)}/${path}/:${idProp}`;
}

/**
 * Builds the target configuration for an admin page to create an entity
 *
 *     buildCreatePageTarget(AdminPages.Users); // => '/admin/users/new'
 *
 * @param {String} adminPage
 */
export function buildCreatePageTarget(adminPage) {
    return `${buildRootPageTarget(adminPage)}/new`;
}

/**
 * Builds a URL (for the <Link to={URL} />) and also maps URL parameters from `entityOrId`
 * if any parameters are present for the page configuration
 *
 *     const userObject = { _id: '12345-abcd', name: '...', ... };
 *     buildPageUrl(AdminPages.Users, userObject); // => '/admin/users/12345-abcd'
 *
 *     // or, passing a string in instead (only works for URLs with a single ID parameter)
 *     buildPageUrl(AdminPages.Users, '12345-abcd'); // => '/admin/users/12345-abcd'
 *
 * @param {String} adminPage
 * @param {Object|String} [entityOrId] An optional value to map URL parameters from
 */
export function buildPageUrl(adminPage, entityOrId) {
    const idProp = ADMIN_PAGE_ID_PROPERTY_OVERRIDES[adminPage] || DEFAULT_ID_PROPERTY;
    let data = entityOrId;

    if (typeof data === 'string') {
        data = { [idProp]: data };
    }

    const target = buildPageTarget(adminPage);
    return buildLinkUrl({ target }, data);
}

/**
 * Returns the entity ID for a given detail page.
 *
 * @param {String} [idProperty="_id"] The ID property to use in the URL
 */
export function useEntityIdParam(idProperty = DEFAULT_ID_PROPERTY) {
    const params = useParams();

    return params?.[idProperty];
}

export function buildPageId(adminPage) {
    // todo - enhance this
    return adminPage;
}
